import React from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { Button } from '../ui/button';
import { LogOut, Menu } from 'lucide-react';
import { LanguageSwitcher } from '../LanguageSwitcher';
import { useTranslation } from '@/i18n';
import type { MerchantShellProfile } from '@/types/common.types';

interface HeaderProps {
	className?: string;
	onMobileMenuToggle?: () => void;
	/** Merchant shell profile for display. If not provided, shows defaults. */
	profile?: MerchantShellProfile | null;
}

export const Header: React.FC<HeaderProps> = ({ onMobileMenuToggle, profile }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const role = profile?.userRole || '';
	const displayName = profile?.displayName || '';

	const handleLogout = async () => {
		try {
			await supabase.auth.signOut();
		} finally {
			await navigate('/merchant/login');
		}
	};

	const onLogoutClick = () => {
		void handleLogout();
	};

	const getRoleDisplayName = (role: string) => {
		switch (role) {
			case 'user':
				// On web, 'user' role means merchant user (merchant role is in user_merchants)
				return t('common.roles.merchant');
			case 'super_admin':
				return t('common.roles.superAdmin');
			default:
				return t('common.roles.merchant');
		}
	};

	return (
		<header className='bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm w-full'>
			<div className='px-4 sm:px-6 lg:px-8 w-full'>
				<div className='flex justify-between items-center h-16 w-full'>
					{/* Left side */}
					<div className='flex items-center space-x-4'>
						{/* Mobile menu button */}
						<Button variant='ghost' size='sm' className='md:hidden' onClick={onMobileMenuToggle}>
							<Menu className='h-5 w-5' />
						</Button>

						{/* Logo and title */}
						<div className='flex items-center space-x-3'>
							<img
								src='/logo.svg'
								alt={t('common.brand.name')}
								className='w-10 h-10 object-contain drop-shadow-sm'
							/>
							<div className='hidden sm:block'>
								<h1 className='text-xl font-semibold text-gray-900'>{t('common.brand.name')}</h1>
							</div>
						</div>
					</div>

					{/* Right side */}
					<div className='flex items-center space-x-4'>
						{/* Language Switcher */}
						<LanguageSwitcher />

						{/* User info */}
						<div className='flex items-center space-x-3'>
							<div className='hidden sm:block text-right'>
								<p className='text-sm font-medium text-gray-900'>
									{displayName || t('common.mobileMenu.merchantLabel')}
								</p>
								<p className='text-xs text-gray-500'>{getRoleDisplayName(role)}</p>
							</div>

							{/* Logout button */}
							<Button
								variant='ghost'
								size='sm'
								onClick={onLogoutClick}
								className='text-gray-500 hover:text-red-600'
							>
								<LogOut className='h-5 w-5' />
								<span className='sr-only'>{t('auth.logout')}</span>
							</Button>
						</div>
					</div>
				</div>
			</div>
		</header>
	);
};
